/**
 * LightLevelFader - a fader on the target's master level.
 *
 * Reuses `Fader`'s markup and CSS verbatim (`.fader` / `-track` / `-fill` /
 * `-handle` / `-readout`) so a lighting fader and a MIDI fader are the same
 * object under the finger, with the same grab-to-unlock guard behaviour. Zero
 * new CSS by design - a second fader that looked or felt different would be a
 * second thing to learn for no gain.
 *
 * ## Three things it does differently from Fader, each for a reason
 *
 * 1. **0-255, not 0-127.** DMX is a byte. Sending a MIDI-ranged 0-127 would
 *    cap the rig at half brightness, and the operator would conclude their
 *    lights are dim rather than that the fader is wrong.
 *
 * 2. **It writes `/state` with `level` ALONE.** Not `/color`. That is the
 *    whole reason `/state` exists: `/color` requires an `rgb` and defaults
 *    `intensity` to full, so a level-only change would have to be smuggled
 *    through a half-brightness colour - the fixture being told a COLOUR when
 *    the operator moved a LEVEL. On a fixture with a real dimmer channel
 *    OPBridge drives the dimmer; on a dimmer-less one (the SlimPARs) it bakes
 *    the level into the RGB, so the fader dims either way without this
 *    component needing to know which kind it is pointed at.
 *
 * 3. **A group write is N requests**, so the throttle matters more here than
 *    on a MIDI fader: one drag frame against a five-fixture group is five
 *    POSTs. The ~60Hz coalescing is inherited from Fader and is load-bearing,
 *    not incidental.
 *
 * ## The value it shows is what YOU set, not what the rig is
 *
 * `item.value` persists the last authored position so the handle renders
 * where it was left rather than snapping to zero on reload. That is a record
 * of the last command, NOT a readback - a timeline or an effect can move the
 * real level underneath it and this fader will not know. Phase 4
 * (`/dmx/fixtures/resolved`) is what closes that loop. Until it does, the
 * readout is deliberately labelled `level` rather than anything implying the
 * rig confirmed it, the same honesty boundary DEC-124 draws for MIDI send
 * lighting.
 */

import { createSignal, createEffect, onCleanup } from 'solid-js';
import { sendDmxState } from '../opbridge/dispatch.ts';
import { editOwnsGesture, isEditMode, editItem, type LightLevelItem, type LayoutColor } from '../layout/store.ts';
import { GUARD_GRAB_HOLD_MS, GUARD_GRAB_MOVE_TOL } from '../performance/store.ts';
import { resolveTarget, targetSubfixture, describeTarget, targetDeadReason } from '../lighting/targets.ts';
import { watchLighting, liveLevel, heldByOther } from '../lighting/liveState.ts';

interface LightLevelFaderProps {
  item: LightLevelItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Coalesce dispatches to ~60Hz. Inherited from Fader and more important
 *  here: one frame against a group is one POST per fixture. */
const DISPATCH_INTERVAL_MS = 16;

/** DMX is a byte. */
const MAX_LEVEL = 255;

export function LightLevelFader(props: LightLevelFaderProps) {
  watchLighting();
  const [dragging, setDragging] = createSignal(false);
  // In-drag value lives HERE, not in the store - writing the store every move
  // replaces the item object, App.tsx's <For> recreates the row, and the drag
  // dies after one event with its pointer capture. That bug shipped on the
  // MIDI fader and is documented in Fader.tsx; not repeating it.
  const [liveValue, setLiveValue] = createSignal<number | null>(null);
  const shownValue = () => liveValue() ?? props.item.value;
  let lastDispatchMs = 0;
  let pendingValue: number | null = null;
  let padEl: HTMLDivElement | undefined;

  const deadReason = () => targetDeadReason(props.item.target);

  // Show-mode guard (SD-5): a brush sends nothing; a long-press grabs.
  const [grabbed, setGrabbed] = createSignal(false);
  let grabTimer: number | undefined;
  let grabStartX = 0;
  let grabStartY = 0;
  function beginPendingGrab(x: number, y: number): void {
    grabStartX = x;
    grabStartY = y;
    if (grabTimer !== undefined) clearTimeout(grabTimer);
    grabTimer = window.setTimeout(() => {
      grabTimer = undefined;
      setGrabbed(true);
      setDragging(true);
      commit(valueFromPointer(grabStartX, grabStartY));
    }, GUARD_GRAB_HOLD_MS);
  }
  function cancelPendingGrab(): void {
    if (grabTimer !== undefined) { clearTimeout(grabTimer); grabTimer = undefined; }
  }
  onCleanup(() => { if (grabTimer !== undefined) clearTimeout(grabTimer); });
  createEffect(() => { if (isEditMode()) { cancelPendingGrab(); setGrabbed(false); } });

  function isVertical(): boolean {
    return props.item.vertical !== false;
  }

  function valueFromPointer(clientX: number, clientY: number): number {
    if (!padEl) return shownValue();
    const rect = padEl.getBoundingClientRect();
    let frac: number;
    if (isVertical()) {
      frac = 1 - (clientY - rect.top) / rect.height;
    } else {
      frac = (clientX - rect.left) / rect.width;
    }
    frac = Math.max(0, Math.min(1, frac));
    return Math.round(frac * MAX_LEVEL);
  }

  function dispatchValue(value: number): void {
    const now = performance.now();
    if (now - lastDispatchMs < DISPATCH_INTERVAL_MS) {
      pendingValue = value;
      return;
    }
    lastDispatchMs = now;
    pendingValue = null;
    const targets = resolveTarget(props.item.target);
    if (targets.length === 0) return;
    const sub = targetSubfixture(props.item.target);
    // Fire-and-forget, deliberately unawaited: a drag frame that waited on
    // five HTTP round trips would stutter the handle. Failures surface on the
    // NEXT discrete action rather than as a toast per frame - a fader that
    // toasts at 60Hz during a dead bridge is unusable, and the bridge pill
    // already carries "we are offline".
    void sendDmxState(
      targets.map((f) => f.id),
      {
        level: value,
        // Power tracks the level, so the bottom of the fader is a real
        // blackout and any upward move brings the light back. Without this,
        // pulling a fader up on a resting (`on:false`) fixture stores a level
        // and shows nothing - the same defect the colour button had, measured
        // on the live resolver.
        //
        // Sending `on:false` at zero rather than just a zero level is the
        // better of the two: both go dark, but the power form makes OPBridge
        // REMEMBER the colour, so dragging back up restores what was there
        // instead of nothing.
        on: value > 0,
        ...(sub !== undefined ? { subfixture: sub } : {}),
      },
    );
  }

  function commit(value: number): void {
    setLiveValue(value);
    dispatchValue(value);
  }

  function persist(): void {
    const v = liveValue();
    if (v !== null) editItem(props.item.id, { value: v } as Partial<LightLevelItem>);
    setLiveValue(null);
  }

  function onPointerDown(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    e.preventDefault();
    // A dead target still DRAGS (the handle moves, the value persists) but
    // sends nothing - `dispatchValue` bails on an empty resolution. That is
    // deliberate: disabling the whole control would make an operator who
    // renamed a group think the fader itself broke, and the readout already
    // names the reason.
    const guarded = !isEditMode() && !!props.item.guard;
    if (guarded && !grabbed()) {
      e.currentTarget.setPointerCapture(e.pointerId);
      beginPendingGrab(e.clientX, e.clientY);
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    commit(valueFromPointer(e.clientX, e.clientY));
  }

  function onPointerMove(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      if (Math.abs(e.clientX - grabStartX) > GUARD_GRAB_MOVE_TOL ||
          Math.abs(e.clientY - grabStartY) > GUARD_GRAB_MOVE_TOL) {
        cancelPendingGrab();
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    commit(valueFromPointer(e.clientX, e.clientY));
  }

  function onPointerUp(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      cancelPendingGrab();
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    setGrabbed(false);
    // Flush a value the throttle swallowed, so the final position always
    // reaches the rig even when the last move fell inside the window. Without
    // this a slow settle at the end of a drag leaves the lights one frame
    // behind where the handle stopped.
    if (pendingValue !== null) {
      lastDispatchMs = 0;
      commit(pendingValue);
    }
    persist();
  }

  function handleOffsetPct(): number {
    return (shownValue() / MAX_LEVEL) * 100;
  }

  /** The level actually on the wire, agreed across every targeted fixture.
   *  `undefined` when the state is stale, when the targets disagree (a group
   *  mid-fade genuinely has no single level), or when there is nothing to
   *  read - all of which mean "do not show a second number", never "show
   *  zero". */
  const liveValueNow = (): number | undefined => {
    const targets = resolveTarget(props.item.target);
    if (targets.length === 0) return undefined;
    const levels = targets.map((f) => liveLevel(f.id));
    const first = levels[0];
    if (first === undefined) return undefined;
    return levels.every((l) => l === first) ? first : undefined;
  };

  /** Who else is driving these lights. Same rule as the buttons: reported
   *  only when every targeted fixture agrees. */
  const liveNote = (): string | undefined => {
    const targets = resolveTarget(props.item.target);
    if (targets.length === 0) return undefined;
    const holders = targets.map((f) => heldByOther(f.id));
    const first = holders[0];
    return first && holders.every((h) => h === first) ? first : undefined;
  };

  /** Target first, holder behind it - same rule as the buttons. The fader's
   *  readout is a narrow column, so this one truncates with an ellipsis
   *  rather than wrapping (see `[data-stacked]` in app.css): half a fixture
   *  name still identifies the light, no value at all identifies nothing. */
  const faderLabel = (): string => {
    const where = describeTarget(props.item.target);
    const note = liveNote();
    return note ? `${where} · ${note}` : where;
  };

  const name = () => props.item.name?.trim() ?? '';
  const showName = () => !props.item.hideName;
  const showInfo = () => !props.item.hideLabel;

  return (
    <div
      class="fader"
      data-vertical={isVertical() ? 'true' : 'false'}
      data-token={COLOR_TOKEN[props.item.color]}
      data-dragging={dragging() ? 'true' : 'false'}
      data-grabbed={grabbed() ? 'true' : 'false'}
      ref={padEl}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div class="fader-track" />
      <div
        class="fader-fill"
        style={
          isVertical()
            ? { height: `${handleOffsetPct()}%` }
            : { width: `${handleOffsetPct()}%` }
        }
      />
      <div
        class="fader-handle"
        style={
          isVertical()
            ? { bottom: `calc(${handleOffsetPct()}% - 8px)` }
            : { left: `calc(${handleOffsetPct()}% - 8px)` }
        }
      />
      {showName() && name() && (
        <div class="fader-name">{name()}</div>
      )}
      {/* `data-stacked` - see the CSS. Side-by-side works for `cc 60 - ch 1`
          plus a two-digit value and does not work for a fixture name plus two
          numbers; on a 64px-wide box they wrap into each other. */}
      {showInfo() && (
        <div class="fader-readout" data-stacked="true">
          <span class="fader-cc">{deadReason() ?? faderLabel()}</span>
          {/* TWO numbers when they disagree, and the order matters: the LIVE
              one is what the lights are doing, the handle position is what
              this fader last asked for. They diverge whenever a timeline, an
              effect or another control has moved the rig since - which on
              this operator's rig is the normal case, not an edge one.
              Showing only the handle position would be the fader quietly
              lying about the stage.

              DELIBERATELY NOT driving the handle from the live value. A
              motorised-fader behaviour is what a lighting desk does and is
              tempting here, but it means the control can move under the
              operator's finger mid-set, and on a single touch device with no
              motor to feel that is a fight rather than feedback. Displaying
              the divergence gives the same information and takes nothing
              away. Revisit only if the operator asks for it. */}
          <span class="fader-value">
            {liveValueNow() !== undefined && liveValueNow() !== shownValue()
              ? `${liveValueNow()} / ${shownValue()}`
              : shownValue()}
          </span>
        </div>
      )}
    </div>
  );
}
